'use client';

import { useTranslations } from 'next-intl';
import { useRouter, useSearchParams } from 'next/navigation';
import { useRef } from 'react';

import {
  BottomBar,
  ProductSelectionSection,
  UltracareInfoSection,
} from 'components/ring-air';
import { Contents } from 'components/ring-air/Contents';
import { RingSelectionContextProvider } from 'components/ring-air/RingSelectionContext';
import { MCFRing } from 'components/ring-air/MCFRing';
import { NonMCFRing } from 'components/ring-air/NonMCFRing';
import { EsRetailSaleModal } from 'components/ring-air/EsRetailSaleModal';
import { AuRetailSaleModal } from 'components/ring-air/AuRetailSaleModal';
import { SecondaryNavbar_RingAir } from 'components/ring-air/SecondaryNavbar';
import { BgBlurredText } from 'components/ring-air/BgBlurredText';
import { Capabilities } from 'components/ring-air/Capabilities';
import { SolidBgTextSection } from 'components/ring-air/SolidBgTextSection';
import { UnderstandYourRecovery } from 'components/ring-air/UnderstandYourRecovery';
import { UnderstandYourSleep } from 'components/ring-air/UnderstandYourSleep';
import { ReviewsSection } from 'components/ring-air/ReviewsSection';
import { isReviewsSectionEnabled } from 'utils/featureFlags';
import { NewComparison } from 'components/NewComparison';
import { ProductWizard } from 'components/shared/ProductWizard';
import { FAQSection } from 'components/shared/FAQSection';
import { TechnicalSpecs } from 'components/technicalSpecs/TechnicalSpecs';
import { JsonLd } from 'components/shared';
import { RetouchFlowProvider } from 'components/shared/RetouchFlow';
import {
  ClaimSizingKitPopup,
  ClaimSizingKitPopupHandle,
} from 'components/shared/ClaimSizingKitPopup';
import { shouldShowSizingKitPopup } from 'components/shared/ClaimSizingKitPopup/constants';

import { Currency } from 'utils/Currency';
import { ShopifyStore } from 'lib/commerce/shopify/shopifyHelper';
import { isMCFCountry } from 'lib/region/countryUtils';
import { AirRingColors, RingSizes } from 'content/products/ring';
import {
  getRingBuyPageFaqData,
  getRingBuyPageFaqData_IN,
  ringBuyPageFaqData_AE,
} from 'content/products/ring/faqData';
import { MasterTimelineProvider } from 'contexts/MasterTimelineContext';
import { getRingPricingSchemas } from 'lib/seo/meta/pricing/ring';
import { ShippingEstimateObject } from 'components/ring-air/utils/types';

import { useRegionLocale } from 'contexts/RegionLocaleContext';
import { getOffersForRegion } from 'components/ring-air/utils/offersByRegion';

type RingBuyPageProps = {
  region: string;
  language: string;
  defaultSize: RingSizes | null;
  defaultColor: AirRingColors | null;
  defaultUhx: boolean;
  estimates?: ShippingEstimateObject;
  store: ShopifyStore;
  currency: Currency;
};

const USRingBuy = ({
  defaultSize,
  defaultColor,
  defaultUhx,
  estimates,
  store,
  currency,
  region,
}: RingBuyPageProps) => {
  const router = useRouter();
  const searchParams = useSearchParams();
  const { regionSlug, language } = useRegionLocale();
  const t = useTranslations('ring');
  // useGlobalUI({ headerCollaspable: true });

  const ringSchemas = getRingPricingSchemas({
    locale: region.toLowerCase(),
    country: region.toLowerCase(),
    regionSlug,
    store,
    currency,
    defaultColor: defaultColor ?? AirRingColors.RAW_TITANIUM,
    t,
  });

  const { offersData, secondaryOfferData } = getOffersForRegion(region, {
    t,
    regionSlug,
    router: {
      push: (url: string) => router.push(url),
      query: Object.fromEntries(searchParams?.entries() ?? []),
    },
  });

  const reviewsEnabled = isReviewsSectionEnabled({ region, language });

  return (
    <>
      <JsonLd data={ringSchemas} />
      <RingSelectionContextProvider
        currency={currency}
        defaultSize={defaultSize}
        defaultColor={defaultColor ?? AirRingColors.RAW_TITANIUM}
        defaultUhx={defaultUhx}
        estimates={estimates}
        notServicable={true}
        notServicableMessage={t('ring.buyPage.notServicableMessage')}
      >
        <ProductSelectionSection
          description={t('ring.buyPage.productDescription')}
          secondaryOfferData={secondaryOfferData}
          offersData={offersData}
        />
        {/* CONFIG: greyscale for US ban - remove the div when want to remove */}
        <div style={{ filter: 'grayscale(1)' }}>
          <SecondaryNavbar_RingAir />
          <Contents htmlId="ring-contents" />
          {reviewsEnabled && <ReviewsSection htmlId="ring-reviews" />}
          <BgBlurredText />
          <UnderstandYourSleep htmlId="ring-sleep" />
          <UnderstandYourRecovery />
          <UltracareInfoSection />
          <SolidBgTextSection />
          <Capabilities htmlId="ring-capabilities" />
          <TechnicalSpecs htmlId="ring-specs" />
          <ProductWizard title={t('ring.productWizard.contentA')} />
          <FAQSection faqData={getRingBuyPageFaqData(t)} htmlId="ring-faq" />
        </div>
        <BottomBar />
      </RingSelectionContextProvider>
    </>
  );
};

/**
 * India-specific ring buy page — EMI copies, IN FAQ
 */
const INRingBuy = ({
  defaultSize,
  defaultColor,
  defaultUhx,
  estimates,
  store,
  currency,
  region,
}: RingBuyPageProps) => {
  const router = useRouter();
  const searchParams = useSearchParams();
  const { regionSlug, language } = useRegionLocale();
  const t = useTranslations('ring');
  // useGlobalUI({ headerCollaspable: true });
  const sizingKitRef = useRef<ClaimSizingKitPopupHandle>(null);

  const ringSchemas = getRingPricingSchemas({
    locale: region.toLowerCase(),
    country: region.toLowerCase(),
    regionSlug,
    store,
    currency,
    defaultColor: defaultColor ?? AirRingColors.RAW_TITANIUM,
    t,
  });

  const { offersData, secondaryOfferData } = getOffersForRegion(region, {
    t,
    regionSlug,
    router: {
      push: (url: string) => router.push(url),
      query: Object.fromEntries(searchParams?.entries() ?? []),
    },
  });

  const reviewsEnabled = isReviewsSectionEnabled({ region, language });

  return (
    <>
      <JsonLd data={ringSchemas} />
      <RingSelectionContextProvider
        currency={currency}
        defaultSize={defaultSize}
        defaultColor={defaultColor ?? AirRingColors.RAW_TITANIUM}
        defaultUhx={defaultUhx}
        estimates={estimates}
        onCheckoutStarted={() => sizingKitRef.current?.cancelPopup()}
      >
        <ProductSelectionSection
          description={t('ring.buyPage.productDescription')}
          emiCopies={true}
          secondaryOfferData={secondaryOfferData}
          offersData={offersData}
        />
        <SecondaryNavbar_RingAir />
        <Contents htmlId="ring-contents" />
        {reviewsEnabled && <ReviewsSection htmlId="ring-reviews" />}
        <BgBlurredText />
        <UnderstandYourSleep htmlId="ring-sleep" />
        <UnderstandYourRecovery />
        <UltracareInfoSection />
        <NewComparison htmlId="ring-comparision" />
        <SolidBgTextSection />
        <Capabilities htmlId="ring-capabilities" />
        <TechnicalSpecs htmlId="ring-specs" />
        <ProductWizard title={t('ring.productWizard.contentA')} />
        <FAQSection faqData={getRingBuyPageFaqData_IN(t)} htmlId="ring-faq" />
        <BottomBar />
        {shouldShowSizingKitPopup(region, 'ring_air') && (
          <ClaimSizingKitPopup ref={sizingKitRef} region={region} />
        )}
      </RingSelectionContextProvider>
    </>
  );
};

/**
 * AE-specific ring buy page — Tabby/Tamara/Cashew, AE FAQ
 */
const AERingBuy = ({
  defaultSize,
  defaultColor,
  defaultUhx,
  estimates,
  store,
  currency,
  region,
}: RingBuyPageProps) => {
  const router = useRouter();
  const searchParams = useSearchParams();
  const { regionSlug, language } = useRegionLocale();
  const t = useTranslations('ring');
  // useGlobalUI({ headerCollaspable: true });
  const sizingKitRef = useRef<ClaimSizingKitPopupHandle>(null);

  const ringSchemas = getRingPricingSchemas({
    locale: region.toLowerCase(),
    country: region.toLowerCase(),
    regionSlug,
    store,
    currency,
    defaultColor: defaultColor ?? AirRingColors.RAW_TITANIUM,
    t,
  });

  const { offersData, secondaryOfferData } = getOffersForRegion(region, {
    t,
    regionSlug,
    router: {
      push: (url: string) => router.push(url),
      query: Object.fromEntries(searchParams?.entries() ?? []),
    },
  });

  const reviewsEnabled = isReviewsSectionEnabled({ region, language });

  return (
    <>
      <JsonLd data={ringSchemas} />
      <RingSelectionContextProvider
        currency={currency}
        defaultSize={defaultSize}
        defaultColor={defaultColor ?? AirRingColors.RAW_TITANIUM}
        defaultUhx={defaultUhx}
        estimates={estimates}
        onCheckoutStarted={() => sizingKitRef.current?.cancelPopup()}
      >
        <ProductSelectionSection
          description={t('ring.buyPage.productDescription')}
          secondaryOfferData={secondaryOfferData}
          offersData={offersData}
        />
        <SecondaryNavbar_RingAir />
        <Contents htmlId="ring-contents" />
        {reviewsEnabled && <ReviewsSection htmlId="ring-reviews" />}
        <BgBlurredText />
        <UnderstandYourSleep htmlId="ring-sleep" />
        <UnderstandYourRecovery />
        <UltracareInfoSection />
        <NewComparison htmlId="ring-comparision" />
        <SolidBgTextSection />
        <Capabilities htmlId="ring-capabilities" />
        <TechnicalSpecs htmlId="ring-specs" />
        <ProductWizard title={t('ring.productWizard.contentA')} />
        <FAQSection faqData={ringBuyPageFaqData_AE} htmlId="ring-faq" />
        <BottomBar />
        {shouldShowSizingKitPopup(region, 'ring_air') && (
          <ClaimSizingKitPopup ref={sizingKitRef} region={region} />
        )}
      </RingSelectionContextProvider>
    </>
  );
};

export default function Page(props: RingBuyPageProps) {
  const { region } = props;
  const { regionSlug } = useRegionLocale();
  const t = useTranslations('ring');
  const regionUpper = region.toUpperCase();
  const locale = region.toLowerCase();

  // US/PR — specific layout with greyscale + notServicable
  if (regionUpper === 'US' || regionUpper === 'PR') {
    return (
      <MasterTimelineProvider>
        <RetouchFlowProvider locale={locale}>
          <USRingBuy {...props} />
        </RetouchFlowProvider>
      </MasterTimelineProvider>
    );
  }

  // India — specific layout with EMI
  if (regionUpper === 'IN') {
    return (
      <MasterTimelineProvider>
        <RetouchFlowProvider locale={locale}>
          <INRingBuy {...props} />
        </RetouchFlowProvider>
      </MasterTimelineProvider>
    );
  }

  // UAE & GCC countries — specific layout with Tabby/Tamara
  if (
    regionUpper === 'AE' ||
    regionUpper === 'QA' ||
    regionUpper === 'BH' ||
    regionUpper === 'KW' ||
    regionUpper === 'OM'
  ) {
    return (
      <MasterTimelineProvider>
        <RetouchFlowProvider locale={locale}>
          <AERingBuy {...props} />
        </RetouchFlowProvider>
      </MasterTimelineProvider>
    );
  }

  // MCF countries (EU + GB) — use MCFRing component
  if (isMCFCountry(regionUpper)) {
    const ringSchemas = getRingPricingSchemas({
      locale,
      country: locale,
      regionSlug,
      store: props.store,
      currency: props.currency,
      defaultColor: props.defaultColor ?? AirRingColors.RAW_TITANIUM,
      t,
    });

    return (
      <MasterTimelineProvider>
        <RetouchFlowProvider locale={locale}>
          <JsonLd data={ringSchemas} />
          {regionUpper === 'ES' && <EsRetailSaleModal />}
          <MCFRing {...props} />
        </RetouchFlowProvider>
      </MasterTimelineProvider>
    );
  }

  // All other countries — NonMCFRing (AU, CA, JP, SA, MX, ZA, ROW, etc.)
  const ringSchemas = getRingPricingSchemas({
    locale,
    country: locale,
    regionSlug,
    store: props.store,
    currency: props.currency,
    defaultColor: props.defaultColor ?? AirRingColors.RAW_TITANIUM,
    t,
  });

  return (
    <MasterTimelineProvider>
      <RetouchFlowProvider locale={locale}>
        <JsonLd data={ringSchemas} />
        {regionUpper === 'AU' && <AuRetailSaleModal />}
        <NonMCFRing {...props} />
      </RetouchFlowProvider>
    </MasterTimelineProvider>
  );
}
